import React from 'react';
import { ChevronDownIcon, RotateCcwIcon, SlidersHorizontalIcon } from 'lucide-react';
import { Assumptions } from '../types/compensation';
import { formatDkk } from '../utils/format';
import { NumberField } from './NumberField';
import { TextField } from './TextField';
interface AssumptionsPanelProps {
  assumptions: Assumptions;
  onChange: (patch: Partial<Assumptions>) => void;
  onReset: () => void;
}
export function AssumptionsPanel({
  assumptions,
  onChange,
  onReset
}: AssumptionsPanelProps) {
  const [open, setOpen] = React.useState(false);
  const a = assumptions;
  return <section className="rounded-card border border-line bg-ink-900" data-id="element-1" data-mp="19etkaammligx">
      <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open} aria-controls="unit-economics-body" className="flex w-full items-center gap-4 px-6 py-5 text-left lg:px-7" data-id="element-2" data-mp="19h630q07jwv1">
        <SlidersHorizontalIcon className="h-5 w-5 shrink-0 text-paper-dim" aria-hidden="true" data-id="element-3" data-mp="1eeo8u7ixhvyt" />
        <span className="flex-1" data-id="element-4" data-mp="2x35hughyzbc1">
          <span className="block text-base font-semibold tracking-tight text-paper" data-id="element-5" data-mp="2x46v8rmgk9wx">
            Unit economics
          </span>
          <span className="mt-0.5 block text-sm text-paper-dim" data-id="element-6" data-mp="36zibjedilp6s">
            How the numbers are calculated — and every input behind them
          </span>
        </span>
        <ChevronDownIcon className={['h-5 w-5 shrink-0 text-paper-muted transition-transform duration-200 ease-out', open ? 'rotate-180' : ''].join(' ')} aria-hidden="true" data-id="element-7" data-mp="3azab51kxkl00" />
      </button>

      {open ? <div id="unit-economics-body" className="border-t border-line px-6 py-7 lg:px-7" data-id="element-8" data-mp="3pr1btdg5mp54">
          <div className="space-y-3 text-sm leading-relaxed text-paper-muted" data-id="element-9" data-mp="3urp0hsgwutw6">
            <p data-id="element-10" data-mp="2nehnjgasl84">
              Everything comes from one pool. Every krone you move below the{' '}
              <span className="tnum text-paper" data-id="element-11" data-mp="7lfwttals0eb">
                {formatDkk(a.referenceMonthlySalary)}
              </span>{' '}
              market reference becomes an annual sacrifice pool, and that pool is
              what funds commission and ownership.
            </p>
            <p data-id="element-12" data-mp="2dew49tuo8gbl">
              The pool is split first, then the multipliers apply. Krone
              allocated to commission is multiplied by{' '}
              <span className="tnum text-paper" data-id="element-13" data-mp="28lb2dqpyj274">{a.commissionMultiplier}×</span>{' '}
              and paid as cash commission at 100% of target. Krone allocated to
              equity is multiplied by{' '}
              <span className="tnum text-paper" data-id="element-14" data-mp="2wcq9dwsoi7op">{a.equityMultiplier}×</span> and
              converted to ownership against the company valuation.
            </p>
            <p data-id="element-15" data-mp="2re5co7cmy650">
              Cash and equity are two separate outcomes of the same allocation —
              the equity value is never deducted from the commission. Milestone
              warrants sit outside both figures.
            </p>
          </div>

          <div className="mt-8 space-y-8" data-id="element-16" data-mp="38mg0hmc1ge5x">
            <fieldset data-id="element-17" data-mp="38npyycmszod3">
              <legend className="text-xs uppercase tracking-[0.16em] text-paper-muted" data-id="element-18" data-mp="38pak5bn89uya">
                Salary basis
              </legend>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-19" data-mp="2zvfl8lcp90r3">
                <NumberField label="Reference market salary" value={a.referenceMonthlySalary} step={1000} suffix="DKK / month" onChange={v => onChange({
              referenceMonthlySalary: v
            })} data-id="element-20" data-mp="33vzmo14b2qj1" />
                <NumberField label="Minimum fixed salary" value={a.minFixedSalary} step={1000} suffix="DKK / month" onChange={v => onChange({
              minFixedSalary: v
            })} data-id="element-21" data-mp="2gvs1ogxu7kvf" />
                <NumberField label="Maximum fixed salary" value={a.maxFixedSalary} step={1000} suffix="DKK / month" onChange={v => onChange({
              maxFixedSalary: v
            })} data-id="element-22" data-mp="f2arp6m8i7xi" />
                <div className="grid grid-cols-2 gap-3" data-id="element-23" data-mp="3lmysof7teje4">
                  <NumberField label="Commission ×" value={a.commissionMultiplier} step={0.5} suffix="×" onChange={v => onChange({
                commissionMultiplier: v
              })} data-id="element-24" data-mp="3gmaxriggtiem" />
                  <NumberField label="Equity ×" value={a.equityMultiplier} step={0.5} suffix="×" onChange={v => onChange({
                equityMultiplier: v
              })} data-id="element-25" data-mp="32v62sto1leam" />
                </div>
              </div>
            </fieldset>

            <fieldset data-id="element-26" data-mp="2ny58qjidp78z">
              <legend className="text-xs uppercase tracking-[0.16em] text-paper-muted" data-id="element-27" data-mp="2nwlv70o3xaaw">
                Commission &amp; targets
              </legend>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-28" data-mp="zej9pd3taaca">
                <div className="grid grid-cols-2 gap-3" data-id="element-29" data-mp="zgc9c8g0buas">
                  <NumberField label="Existing min %" value={a.existingRateMin} step={0.5} suffix="%" onChange={v => onChange({
                existingRateMin: v
              })} data-id="element-30" data-mp="13etgut3cb6sq" />
                  <NumberField label="Existing max %" value={a.existingRateMax} step={0.5} suffix="%" onChange={v => onChange({
                existingRateMax: v
              })} data-id="element-31" data-mp="fqsc7v9a8ail" />
                </div>
                <div className="grid grid-cols-2 gap-3" data-id="element-32" data-mp="uewmqby6lfqa">
                  <NumberField label="New min %" value={a.newRateMin} step={0.5} suffix="%" onChange={v => onChange({
                newRateMin: v
              })} data-id="element-33" data-mp="ughha1khwyo5" />
                  <NumberField label="New max %" value={a.newRateMax} step={0.5} suffix="%" onChange={v => onChange({
                newRateMax: v
              })} data-id="element-34" data-mp="3q2s7beje61v4" />
                </div>
                <NumberField label="Existing incremental revenue target" value={a.existingRevenueTarget} step={250_000} suffix="DKK / year" onChange={v => onChange({
              existingRevenueTarget: v
            })} data-id="element-35" data-mp="3uuk3c4lbgzsn" />
                <NumberField label="New business target" value={a.newBusinessTarget} step={250_000} suffix="DKK / year" onChange={v => onChange({
              newBusinessTarget: v
            })} data-id="element-36" data-mp="3h3exgkrcvb9v" />
              </div>
            </fieldset>

            <fieldset data-id="element-37" data-mp="rcid6fbzf2do">
              <legend className="text-xs uppercase tracking-[0.16em] text-paper-muted" data-id="element-38" data-mp="rd1y84wxi6vx">
                Ownership &amp; vesting
              </legend>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-39" data-mp="2easwv4mto568">
                <NumberField label="Company valuation" value={a.companyValuation} step={1_000_000} suffix="DKK" onChange={v => onChange({
              companyValuation: v
            })} data-id="element-40" data-mp="2e7x21p69twm0" />
                <NumberField label="Vested at cliff" value={a.firstYearVestPercent} step={5} suffix="%" onChange={v => onChange({
              firstYearVestPercent: v
            })} data-id="element-41" data-mp="2szz67lfdjbru" />
                <div className="grid grid-cols-2 gap-3" data-id="element-42" data-mp="2o7fgxr7bqs77">
                  <NumberField label="Vesting period" value={a.vestingYears} step={1} suffix="years" onChange={v => onChange({
                vestingYears: v
              })} data-id="element-43" data-mp="2o64h22h024ld" />
                  <NumberField label="Cliff" value={a.cliffMonths} step={1} suffix="months" onChange={v => onChange({
                cliffMonths: v
              })} data-id="element-44" data-mp="1go3gv38p25tg" />
                </div>
              </div>

              <div className="mt-6 border-t border-line-soft pt-4" data-id="element-45" data-mp="11zfmh0al4253">
                <p className="text-[11px] uppercase tracking-[0.14em] text-paper-dim" data-id="element-46" data-mp="1zkbmw99j6kpt">
                  Milestone warrant grants
                </p>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2" data-id="element-47" data-mp="1ukgvm4luft48">
                  {a.milestones.map(m => <TextField key={m.id} label={m.label} value={m.grant} placeholder="To be agreed" onChange={value => onChange({
                milestones: a.milestones.map(item => item.id === m.id ? {
                  ...item,
                  grant: value
                } : item)
              })} data-id="element-48" data-mp="1llvemdw0sm5k" />)}
                </div>
              </div>
            </fieldset>
          </div>

          <div className="mt-8 border-t border-line-soft pt-5" data-id="element-49" data-mp="2zlggpsokxait">
            <button type="button" onClick={onReset} className="inline-flex items-center gap-2 rounded-lg border border-line px-4 py-2 text-sm text-paper-muted transition-colors duration-150 ease-out hover:border-paper-dim hover:text-paper" data-id="element-50" data-mp="2zn1dzq2a37kd">
              <RotateCcwIcon className="h-4 w-4" aria-hidden="true" data-id="element-51" data-mp="3dj52r3udunpg" />
              Reset to Snakker defaults
            </button>
          </div>
        </div> : null}
    </section>;
}